import React from 'react';
import { getDefaultStandardsForRegion, getDefaultDescriptionForRegion, formatStandardsString } from './RegionalTemplateUtils';

// Helper function to determine which organizations a template is best for
const getBestForOrganizations = (template) => {
  if (!template) return 'All organizations';
  
  const name = template.name ? template.name.toLowerCase() : '';
  const industry = template.industryType ? template.industryType.toLowerCase() : '';
  
  if (name.includes('universal')) {
    return 'All organizations seeking to improve resilience maturity, including enterprises, SMEs, non-profits, and government agencies. Ideal for organizations at any stage of their resilience journey.';
  }
  
  if (name.includes('dora') || name.includes('digital operational resilience')) {
    return 'Financial institutions subject to EU DORA regulation, including banks, payment services, insurance companies, investment firms, credit rating agencies, and ICT third-party service providers to financial entities.';
  }
  
  if (name.includes('fca') || name.includes('pra')) {
    return 'UK financial services firms regulated by FCA/PRA, including banks, building societies, credit unions, insurers, investment firms, and financial market infrastructures requiring operational resilience compliance.';
  }
  
  if (name.includes('cirmp') || name.includes('aus')) {
    return 'Australian critical infrastructure operators in sectors such as communications, data storage, defense, energy, financial services, food and grocery, healthcare, transport, and water. Particularly relevant for organizations subject to the Security of Critical Infrastructure Act.';
  }
  
  if (name.includes('ai act')) {
    return 'Organizations developing or deploying AI systems in the EU, including technology companies, research institutions, healthcare providers using AI diagnostics, financial services using algorithmic decision-making, and manufacturers of AI-enabled products.';
  }
  
  if (name.includes('cca')) {
    return 'Critical infrastructure operators in the EU across energy, transport, banking, financial market, health, drinking water, digital infrastructure, and public administration sectors subject to the NIS2 Directive and Critical Entities Resilience Directive.';
  }
  
  // Industry-specific recommendations
  if (industry.includes('aviation')) {
    return 'Aviation and aerospace organizations including airlines, airports, air traffic control services, aircraft manufacturers, maintenance providers, and aviation supply chain companies.';
  }
  
  if (industry.includes('energy') || industry.includes('utilities')) {
    return 'Energy and utilities providers including electricity generation and distribution companies, oil and gas operators, renewable energy producers, water utilities, and waste management organizations.';
  }
  
  if (industry.includes('financial') || industry.includes('banking')) {
    return 'Financial services and banking organizations including retail and investment banks, insurance companies, payment processors, wealth management firms, credit unions, and financial technology companies.';
  }
  
  if (industry.includes('healthcare')) {
    return 'Healthcare providers and medical organizations including hospitals, clinics, pharmaceutical companies, medical device manufacturers, telehealth providers, and healthcare information systems operators.';
  }
  
  if (industry.includes('technology') || industry.includes('tech')) {
    return 'Technology companies and digital service providers including software developers, cloud service providers, telecommunications companies, data centers, IT managed service providers, and cybersecurity firms.';
  }
  
  if (industry.includes('transport') || industry.includes('logistics')) {
    return 'Transportation and logistics companies including freight carriers, shipping companies, railways, public transportation providers, fleet management services, and supply chain operators.';
  }
  
  return 'Organizations in ' + (industry || 'all sectors') + ' seeking to assess and improve their operational resilience capabilities.';
};

/**
 * TemplateDetails component for displaying details of the selected template
 * @param {Object} props - Component props
 * @param {Object} props.templateDetails - Details of the selected template
 * @param {string} props.activeTab - Currently active region tab
 * @param {Object} props.styles - Styles object for consistent styling
 */
const TemplateDetails = ({ templateDetails, activeTab, styles }) => {
  if (!templateDetails) {
    return (
      <div style={styles.detailsContainer}>
        <div style={styles.detailsPlaceholder}>
          <p>Select a template to view details</p>
        </div>
      </div>
    );
  }

  // Get standards text from template or use default based on region
  const rawStandards = templateDetails.standards || 
                      templateDetails.applicableStandards || 
                      getDefaultStandardsForRegion(activeTab, templateDetails.industryType);
  
  // Format standards with proper spacing and commas
  const standards = formatStandardsString(rawStandards);
  
  // Get description from template or use default based on region
  const description = templateDetails.description || 
                      getDefaultDescriptionForRegion(activeTab, templateDetails.industryType);

  return (
    <div style={styles.detailsContainer}>
      <h3 style={styles.detailsTitle}>Template Selected</h3>
      <div style={styles.detailsContent}>
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsSectionTitle}>Name</h4>
          <p style={styles.detailsText}>{templateDetails.name}</p>
        </div>
        
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsSectionTitle}>Description</h4>
          <p style={styles.detailsText}>{description}</p>
        </div>
        
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsSectionTitle}>Industry</h4>
          <p style={styles.detailsText}>{templateDetails.industryType || 'General'}</p>
        </div>
        
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsSectionTitle}>Best For</h4>
          <p style={styles.detailsText}>{getBestForOrganizations(templateDetails)}</p>
        </div>
        
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsSectionTitle}>Standards & Regulations</h4>
          <p style={styles.detailsText}>{standards}</p>
        </div>
        
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsSectionTitle}>Questions</h4>
          <p style={styles.detailsText}>{templateDetails.questionCount || '100+'} questions</p>
        </div>
      </div>
    </div>
  );
};

export default TemplateDetails;
